import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { useServerTable } from '@/hooks/use-server-table';
import { createActivityLogTableColumns } from './activity-log-table-columns';
import { ActivityLogDetailDrawer } from './activity-log-detail-drawer';
import { useGetActivityLogs } from '../../hooks/use-activity-logs';
import { DEFAULT_ACTIVITY_LOGS_ORDER } from '../../graphql/queries';
import { ActivityLog, ActivityLogsPagingResult } from '../../types/activity-log.types';
import { PageHeader } from '@/components/core';

export const LogsPage = () => {
  const { t } = useTranslation();

  // The filters are the columns' own (header filters, or the filter dialog on a phone).
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_ACTIVITY_LOGS_ORDER,
  });
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // The header spot the cards' filter button is rendered into on a phone.
  const [filterSlot, setFilterSlot] = useState<HTMLSpanElement | null>(null);

  const { data, isLoading, error } = useGetActivityLogs({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: columnWhere,
    order,
  });
  const result = (data as { ActivityLogs?: ActivityLogsPagingResult })?.ActivityLogs;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const columns = createActivityLogTableColumns({ t });

  const handleRowClick = (log: ActivityLog) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('LOGS_TITLE')}
        beforeActions={<span ref={setFilterSlot} className="contents md:hidden" />}
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
        mobileFilterSlot={filterSlot}
      />

      <ActivityLogDetailDrawer
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) setSelectedLog(null);
        }}
        log={selectedLog}
      />
    </div>
  );
};
